/*
 * Marbles ハート（いいね）
 *
 * 設置場所は商品詳細ページといいね一覧ページだけ。商品一覧カード・検索結果・
 * 関連商品には出さないため、それらのテンプレートには手を入れていない。
 *
 * 保存先は端末内の localStorage のみ。ログイン、外部サービス、有料アプリ、
 * 計測タグには一切つながない。保存するのは商品ID・バリアントID（主キー）と
 * handle（商品の取り直し用）だけで、価格・在庫・商品名は保存しない。
 * 一覧に出す値は開くたびに Shopify から取り直すので、値下げや完売がそのまま出る。
 *
 * 保存形式は v2（バリアント単位）。v1（商品単位）で保存された端末は、初回に
 * 一度だけ v2 へ引き継ぐ。v1 のキーは消さずに残す（万一の戻し用）。
 * 引き継いだ項目はバリアント未確定（vid: ''）で入り、一覧を描くときに確定させる。
 *   - バリアントが1つしかない商品 … その場で自動的に確定させる（画面上の操作なし）
 *   - 複数ある商品             … 行にセレクトを出し、選ばれた時点で確定させる
 *
 * 他機能から利用するときの入口:
 *   window.MarblesHeartLikes.list() / has(id, vid) / toggle(id, handle, vid) / count()
 *   document.addEventListener('marbles:likes:change', function (e) { e.detail });
 *
 * 将来アカウント同期を足すときは、この change イベントを購読する側を追加する。
 * このファイル自体は外部ホストへの書き込みを行わない（同一オリジンのカートのみ）。
 */
(function (window, document) {
  'use strict';

  var STORAGE_KEY = 'marbles:likes:v2';
  var LEGACY_STORAGE_KEY = 'marbles:likes:v1';
  var SCHEMA_VERSION = 2;
  var LEGACY_SCHEMA_VERSION = 1;
  var MAX_ITEMS = 100;
  var CHANGE_EVENT = 'marbles:likes:change';
  var CART_CHANGE_EVENT = 'theme:cart:change';
  var POP_CLASS = 'mb-heart--pop';
  var LIKED_CLASS = 'is-liked';
  var FETCH_BATCH = 6;
  /* 一括投入は1点ずつ順番に送る。まとめて送ると1点でも弾かれた時に全部が入らず、
     どれが駄目だったのかも返せないため。上限は誤操作よけの安全弁。 */
  var ADD_LIMIT = 50;
  var toastTimer = null;

  /* ======================================================================
     保存
     ====================================================================== */

  var memoryRecord = null; /* localStorage が使えない端末でも操作だけは通す */
  var storageChecked = false;
  var storageUsable = false;

  function getStorage() {
    if (storageChecked) {
      return storageUsable ? window.localStorage : null;
    }
    storageChecked = true;
    try {
      var probeKey = STORAGE_KEY + ':probe';
      window.localStorage.setItem(probeKey, '1');
      window.localStorage.removeItem(probeKey);
      storageUsable = true;
    } catch (error) {
      storageUsable = false;
    }
    return storageUsable ? window.localStorage : null;
  }

  function isArray(value) {
    return Object.prototype.toString.call(value) === '[object Array]';
  }

  function normalizeId(value) {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim();
  }

  function normalizeHandle(value) {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim();
  }

  /* 1行を指す鍵。バリアント未確定の行は vid が空文字になる */
  function entryKey(id, vid) {
    return normalizeId(id) + '@' + normalizeId(vid);
  }

  function emptyRecord() {
    return { v: SCHEMA_VERSION, items: [] };
  }

  /* 壊れた値・別バージョン・重複を落として、常に扱える形にそろえる */
  function sanitize(record) {
    var clean = emptyRecord();
    if (!record || record.v !== SCHEMA_VERSION || !isArray(record.items)) {
      return clean;
    }
    var seen = {};
    for (var i = 0; i < record.items.length; i++) {
      var raw = record.items[i];
      if (!raw || typeof raw !== 'object') {
        continue;
      }
      var id = normalizeId(raw.id);
      if (!id) {
        continue;
      }
      var vid = normalizeId(raw.vid);
      var key = '#' + entryKey(id, vid);
      if (seen[key]) {
        continue;
      }
      seen[key] = true;
      var at = typeof raw.at === 'number' && isFinite(raw.at) ? raw.at : 0;
      clean.items.push({ id: id, vid: vid, handle: normalizeHandle(raw.handle), at: at });
      if (clean.items.length >= MAX_ITEMS) {
        break;
      }
    }
    return clean;
  }

  /* v1（商品ID単位）の保存値を v2 の形に読み替える。
     バリアントはこの時点では決められないので未確定のまま持ち越し、一覧で確定させる。
     v1 のキー自体は書き換えも削除もしない。 */
  function readLegacy(store) {
    var raw;
    try {
      raw = store.getItem(LEGACY_STORAGE_KEY);
    } catch (error) {
      return null;
    }
    if (!raw) {
      return null;
    }
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      return null;
    }
    if (!parsed || parsed.v !== LEGACY_SCHEMA_VERSION || !isArray(parsed.items)) {
      return null;
    }
    var record = emptyRecord();
    var seen = {};
    for (var i = 0; i < parsed.items.length; i++) {
      var item = parsed.items[i];
      if (!item || typeof item !== 'object') {
        continue;
      }
      var id = normalizeId(item.id);
      if (!id || seen['#' + id]) {
        continue;
      }
      seen['#' + id] = true;
      var at = typeof item.at === 'number' && isFinite(item.at) ? item.at : 0;
      record.items.push({ id: id, vid: '', handle: normalizeHandle(item.handle), at: at });
      if (record.items.length >= MAX_ITEMS) {
        break;
      }
    }
    return record.items.length ? record : null;
  }

  function read() {
    var store = getStorage();
    if (!store) {
      return sanitize(memoryRecord);
    }
    var raw;
    try {
      raw = store.getItem(STORAGE_KEY);
    } catch (error) {
      return sanitize(memoryRecord);
    }
    if (raw) {
      var parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        return emptyRecord();
      }
      return sanitize(parsed);
    }
    /* v2 の値がまだ無い端末だけ、一度だけ v1 を引き継ぐ。
       引き継いだ直後に v2 を書くので、全部消したあとに復活することはない。 */
    var migrated = readLegacy(store);
    if (migrated) {
      write(migrated);
      return sanitize(migrated);
    }
    return emptyRecord();
  }

  function write(record) {
    memoryRecord = record;
    var store = getStorage();
    if (!store) {
      return false;
    }
    try {
      store.setItem(STORAGE_KEY, JSON.stringify(record));
      return true;
    } catch (error) {
      /* 容量超過などでも画面の操作は続けられるようにする */
      return false;
    }
  }

  function indexOfEntry(items, id, vid) {
    var key = entryKey(id, vid);
    for (var i = 0; i < items.length; i++) {
      if (entryKey(items[i].id, items[i].vid) === key) {
        return i;
      }
    }
    return -1;
  }

  /* ======================================================================
     公開API
     ====================================================================== */

  function list() {
    return read().items.slice();
  }

  function ids() {
    var items = read().items;
    var out = [];
    for (var i = 0; i < items.length; i++) {
      out.push(items[i].id);
    }
    return out;
  }

  function keys() {
    var items = read().items;
    var out = [];
    for (var i = 0; i < items.length; i++) {
      out.push(entryKey(items[i].id, items[i].vid));
    }
    return out;
  }

  function count() {
    return read().items.length;
  }

  function has(id, vid) {
    var key = normalizeId(id);
    if (!key) {
      return false;
    }
    return indexOfEntry(read().items, key, vid) > -1;
  }

  /* バリアントを問わず、その商品がどれか1つでも入っているか */
  function hasProduct(id) {
    var key = normalizeId(id);
    if (!key) {
      return false;
    }
    var items = read().items;
    for (var i = 0; i < items.length; i++) {
      if (items[i].id === key) {
        return true;
      }
    }
    return false;
  }

  function add(id, handle, vid) {
    var key = normalizeId(id);
    if (!key) {
      return false;
    }
    var variantId = normalizeId(vid);
    var record = read();
    var at = indexOfEntry(record.items, key, variantId);
    var entry = { id: key, vid: variantId, handle: normalizeHandle(handle), at: Date.now() };
    if (at > -1) {
      /* handle は商品の取り直しに使うので、押されるたびに最新に更新する */
      if (!entry.handle) {
        entry.handle = record.items[at].handle;
      }
      entry.at = record.items[at].at || entry.at;
      record.items.splice(at, 1);
    }
    record.items.unshift(entry);
    if (record.items.length > MAX_ITEMS) {
      record.items.length = MAX_ITEMS;
    }
    write(record);
    return true;
  }

  function remove(id, vid) {
    var key = normalizeId(id);
    if (!key) {
      return false;
    }
    var record = read();
    var at = indexOfEntry(record.items, key, vid);
    if (at < 0) {
      return false;
    }
    record.items.splice(at, 1);
    write(record);
    return true;
  }

  function clear() {
    write(emptyRecord());
  }

  function toggle(id, handle, vid) {
    if (has(id, vid)) {
      remove(id, vid);
      return false;
    }
    add(id, handle, vid);
    return true;
  }

  /* バリアント未確定の行に、決まったバリアントを書き込む。
     同じバリアントの行がすでにある場合は1行にまとめ、古い登録日のほうを残す。
     戻り値: 'resolved' | 'merged' | ''（対象なし） */
  function resolveVariant(id, variantId) {
    var productId = normalizeId(id);
    var vid = normalizeId(variantId);
    if (!productId || !vid) {
      return '';
    }
    var record = read();
    var pending = indexOfEntry(record.items, productId, '');
    if (pending < 0) {
      return '';
    }
    var existing = indexOfEntry(record.items, productId, vid);
    var entry = record.items[pending];
    var outcome = 'resolved';
    if (existing > -1) {
      var kept = record.items[existing];
      if (entry.at && (!kept.at || entry.at < kept.at)) {
        kept.at = entry.at;
      }
      if (!kept.handle) {
        kept.handle = entry.handle;
      }
      record.items.splice(pending, 1);
      outcome = 'merged';
    } else {
      entry.vid = vid;
      if (!entry.at) {
        entry.at = Date.now();
      }
    }
    write(record);
    return outcome;
  }

  /* ======================================================================
     画面の同期
     ====================================================================== */

  function canUseDom() {
    return !!(document && document.addEventListener && document.querySelectorAll);
  }

  function nodeList(scope, selector) {
    var target = scope && scope.querySelectorAll ? scope : document;
    var found = target.querySelectorAll(selector);
    var out = [];
    for (var i = 0; i < found.length; i++) {
      out.push(found[i]);
    }
    return out;
  }

  function heartButtons(root) {
    return nodeList(root, '[data-mb-heart]');
  }

  function applyState(button, liked) {
    if (!button || !button.setAttribute) {
      return;
    }
    button.setAttribute('aria-pressed', liked ? 'true' : 'false');
    var label = liked
      ? button.getAttribute('data-mb-heart-label-on')
      : button.getAttribute('data-mb-heart-label-off');
    if (label) {
      button.setAttribute('aria-label', label);
    }
    if (button.classList) {
      if (liked) {
        button.classList.add(LIKED_CLASS);
      } else {
        button.classList.remove(LIKED_CLASS);
      }
    }
  }

  /* 同じバリアントのハートが同じページに何個あっても、まとめて同じ状態にする */
  function syncButtons(root) {
    if (!canUseDom()) {
      return;
    }
    var liked = {};
    var stored = read().items;
    for (var i = 0; i < stored.length; i++) {
      liked['#' + entryKey(stored[i].id, stored[i].vid)] = true;
    }
    var buttons = heartButtons(root);
    for (var b = 0; b < buttons.length; b++) {
      var id = normalizeId(buttons[b].getAttribute('data-mb-heart-id'));
      var vid = normalizeId(buttons[b].getAttribute('data-mb-heart-variant-id'));
      applyState(buttons[b], !!liked['#' + entryKey(id, vid)]);
    }
  }

  /* ----------------------------------------------------------------------
     商品ページのバリアント追従
     テーマは選択が変わるたび theme:variant:change を投げ、同時に
     [data-product-form] の [name="id"] を書き替える。前者を合図に、後者を正とする。
     ---------------------------------------------------------------------- */

  function formVariantId(button) {
    var node = button;
    while (node && node.querySelector) {
      var input = node.querySelector('[data-product-form] [name="id"]');
      if (input && input.value) {
        return normalizeId(input.value);
      }
      node = node.parentNode;
    }
    return '';
  }

  function applyVariant(button, variantId) {
    if (!button || !button.setAttribute) {
      return;
    }
    var vid = normalizeId(variantId) || formVariantId(button);
    if (!vid) {
      return;
    }
    button.setAttribute('data-mb-heart-variant-id', vid);
  }

  /* 追従対象は商品ページのハートだけ。一覧の行はJSが直接値を入れる */
  function syncVariantHearts(scope, variantId) {
    if (!canUseDom()) {
      return;
    }
    var buttons = nodeList(scope, '[data-mb-heart-follow-variant]');
    if (!buttons.length && scope !== document) {
      buttons = nodeList(document, '[data-mb-heart-follow-variant]');
    }
    for (var i = 0; i < buttons.length; i++) {
      applyVariant(buttons[i], variantId);
    }
    syncButtons();
  }

  function onVariantChange(event) {
    var detail = event && event.detail;
    var variantId = detail && detail.variant ? normalizeId(detail.variant.id) : '';
    var scope = event && event.target && event.target.querySelectorAll ? event.target : document;
    syncVariantHearts(scope, variantId);
  }

  function pop(button) {
    if (!button || !button.classList) {
      return;
    }
    button.classList.remove(POP_CLASS);
    /* クラスを付け直したときに再生させるため、一度レイアウトを読む */
    if (typeof button.offsetWidth === 'number') {
      void button.offsetWidth;
    }
    button.classList.add(POP_CLASS);
    window.setTimeout(function () {
      button.classList.remove(POP_CLASS);
    }, 400);
  }

  function notify(detail) {
    if (!canUseDom() || typeof window.CustomEvent !== 'function') {
      return;
    }
    document.dispatchEvent(new window.CustomEvent(CHANGE_EVENT, {
      bubbles: true,
      detail: detail
    }));
  }

  function showToast(message) {
    if (!canUseDom() || !message || !document.createElement || !document.body || !document.body.appendChild) {
      return;
    }
    var toast = document.querySelector('[data-mb-wishlist-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'mb-wishlist-toast';
      toast.setAttribute('data-mb-wishlist-toast', '');
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.setAttribute('aria-atomic', 'true');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.remove('is-visible');
    if (typeof toast.offsetWidth === 'number') {
      void toast.offsetWidth;
    }
    toast.classList.add('is-visible');
    if (toastTimer && typeof window.clearTimeout === 'function') {
      window.clearTimeout(toastTimer);
    }
    toastTimer = window.setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 3000);
  }

  function showWishlistToast(button, liked) {
    var messageAttribute = liked
      ? 'data-mb-wishlist-added-message'
      : 'data-mb-wishlist-removed-message';
    var message = button && button.getAttribute
      ? button.getAttribute(messageAttribute)
      : '';
    if (!message) {
      message = liked
        ? 'ウィッシュリストに登録しました。'
        : 'ウィッシュリストを解除しました。';
    }
    showToast(message);
  }

  function closestMatch(node, attribute) {
    var current = node;
    while (current && current !== document) {
      if (current.getAttribute && current.hasAttribute && current.hasAttribute(attribute)) {
        return current;
      }
      current = current.parentNode;
    }
    return null;
  }

  function onClick(event) {
    var button = closestMatch(event.target, 'data-mb-heart');
    if (!button) {
      return;
    }
    /* 背後のリンクへ遷移させない */
    event.preventDefault();
    event.stopPropagation();

    var id = normalizeId(button.getAttribute('data-mb-heart-id'));
    if (!id) {
      return;
    }
    var handle = normalizeHandle(button.getAttribute('data-mb-heart-handle'));
    var vid = normalizeId(button.getAttribute('data-mb-heart-variant-id'))
      || formVariantId(button);
    if (vid) {
      button.setAttribute('data-mb-heart-variant-id', vid);
    }
    var liked = toggle(id, handle, vid);
    syncButtons();
    if (liked) {
      pop(button);
    }
    showWishlistToast(button, liked);
    notify({ id: id, vid: vid, handle: handle, liked: liked, count: count(), source: 'heart' });
  }

  /* ======================================================================
     いいね一覧
     価格・在庫はここでShopifyから取り直す。保存済みの古い値は使わない。
     ====================================================================== */

  function rootUrl(container) {
    var configured = container && container.getAttribute
      ? container.getAttribute('data-mb-likes-root-url')
      : '';
    var base = configured
      || (window.theme && window.theme.routes && window.theme.routes.root_url)
      || '/';
    if (base.charAt(base.length - 1) !== '/') {
      base += '/';
    }
    return base;
  }

  /* 画面に出す文言はすべて Liquid 側（ロケール／セクション設定）から渡す */
  function textOf(container, name, fallback) {
    var value = container && container.getAttribute
      ? container.getAttribute('data-mb-likes-text-' + name)
      : '';
    return value || fallback || '';
  }

  function withCount(template, value) {
    return String(template).replace(/\[count\]/g, String(value));
  }

  function sizedImage(url, width) {
    if (!url) {
      return '';
    }
    var src = String(url);
    if (src.indexOf('//') === 0) {
      src = 'https:' + src;
    }
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '¥{{amount_no_decimals}}';
    var vendor = window.pipelineVendor;
    if (vendor && vendor.themeCurrency && typeof vendor.themeCurrency.formatMoney === 'function') {
      try {
        return vendor.themeCurrency.formatMoney(cents, format);
      } catch (error) {
        /* テーマ側の書式に問題があっても一覧は出す */
      }
    }
    return fallbackMoney(cents, format);
  }

  function groupThousands(value, separator) {
    return String(value).replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + separator);
  }

  function fallbackMoney(cents, format) {
    var amount = Number(cents);
    if (!isFinite(amount)) {
      return '';
    }
    return String(format).replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      switch (name) {
        case 'amount':
          return groupThousands((amount / 100).toFixed(2).split('.')[0], ',') + '.' + (amount / 100).toFixed(2).split('.')[1];
        case 'amount_no_decimals':
          return groupThousands(Math.round(amount / 100), ',');
        case 'amount_with_comma_separator':
          return groupThousands((amount / 100).toFixed(2).split('.')[0], '.') + ',' + (amount / 100).toFixed(2).split('.')[1];
        case 'amount_no_decimals_with_comma_separator':
          return groupThousands(Math.round(amount / 100), '.');
        case 'amount_with_space_separator':
          return groupThousands((amount / 100).toFixed(2).split('.')[0], ' ') + ',' + (amount / 100).toFixed(2).split('.')[1];
        case 'amount_no_decimals_with_space_separator':
          return groupThousands(Math.round(amount / 100), ' ');
        default:
          return match;
      }
    });
  }

  function element(tag, className) {
    var node = document.createElement(tag);
    if (className) {
      node.className = className;
    }
    return node;
  }

  function findVariant(product, vid) {
    if (!product || !isArray(product.variants) || !vid) {
      return null;
    }
    for (var i = 0; i < product.variants.length; i++) {
      if (normalizeId(product.variants[i].id) === vid) {
        return product.variants[i];
      }
    }
    return null;
  }

  /* 「（BLACK M）」の部分。単一バリアント商品では出さない */
  function variantLabel(variant) {
    if (!variant) {
      return '';
    }
    if (isArray(variant.options) && variant.options.length) {
      var parts = [];
      for (var i = 0; i < variant.options.length; i++) {
        var value = normalizeId(variant.options[i]);
        if (value) {
          parts.push(value);
        }
      }
      if (parts.length === 1 && parts[0] === 'Default Title') {
        return '';
      }
      return parts.join(' ');
    }
    var title = normalizeId(variant.title);
    return title === 'Default Title' ? '' : title;
  }

  function productUrl(base, product, item) {
    var handle = (product && product.handle) || item.handle;
    if (!handle) {
      return '';
    }
    var url = base + 'products/' + encodeURIComponent(handle);
    if (item.vid) {
      url += '?variant=' + encodeURIComponent(item.vid);
    }
    return url;
  }

  /* 選んだ状態は行を描き直しても保つ。端末には保存しない（画面を離れたら消える） */
  function selectionOf(container) {
    if (!container._mbLikesSelection) {
      container._mbLikesSelection = {};
    }
    return container._mbLikesSelection;
  }

  function placeholderRow() {
    var row = element('li', 'mb-likes__row mb-likes__row--placeholder');
    row.appendChild(element('span', 'mb-likes__cell mb-likes__cell--check'));
    row.appendChild(element('span', 'mb-likes__thumb'));
    var body = element('div', 'mb-likes__body');
    var name = element('p', 'mb-likes__name');
    name.textContent = '　';
    var price = element('p', 'mb-likes__price');
    price.textContent = '　';
    body.appendChild(name);
    body.appendChild(price);
    row.appendChild(body);
    row.appendChild(element('span', 'mb-likes__cell mb-likes__cell--remove'));
    return row;
  }

  function buildThumb(base, product, item, title) {
    var url = productUrl(base, product, item);
    var source = product ? sizedImage(product.featured_image, 320) : '';
    var node = element(url ? 'a' : 'span', 'mb-likes__thumb' + (source ? '' : ' mb-likes__thumb--empty'));
    if (url) {
      node.setAttribute('href', url);
      node.setAttribute('tabindex', '-1');
      node.setAttribute('aria-hidden', 'true');
    }
    if (source) {
      var image = element('img');
      image.setAttribute('src', source);
      image.setAttribute('alt', title);
      image.setAttribute('loading', 'lazy');
      node.appendChild(image);
    }
    return node;
  }

  function buildVariantSelect(container, product, item) {
    var select = element('select', 'mb-likes__select');
    select.setAttribute('data-mb-likes-variant', '');
    select.setAttribute('aria-label', textOf(container, 'choose-placeholder', 'バリアントを選択'));
    var blank = element('option');
    blank.setAttribute('value', '');
    blank.textContent = textOf(container, 'choose-placeholder', 'バリアントを選択');
    select.appendChild(blank);
    var soldOut = textOf(container, 'soldout', 'SOLD OUT');
    for (var i = 0; i < product.variants.length; i++) {
      var variant = product.variants[i];
      var option = element('option');
      option.setAttribute('value', normalizeId(variant.id));
      var label = variantLabel(variant) || normalizeId(variant.title);
      option.textContent = variant.available ? label : label + '（' + soldOut + '）';
      select.appendChild(option);
    }
    return select;
  }

  /*
   * state は次の4つ。
   *   'ok'          … 買える。チェックできる
   *   'soldout'     … バリアントは在るが売り切れ。チェックできない
   *   'pending'     … v1から引き継いだ未確定行。選ばれるまでチェックできない
   *   'unavailable' … 商品が非公開／削除、またはバリアントが消えた。チェックできない
   */
  function rowState(product, item) {
    if (!product) {
      return 'unavailable';
    }
    if (!item.vid) {
      return 'pending';
    }
    var variant = findVariant(product, item.vid);
    if (!variant) {
      return 'unavailable';
    }
    return variant.available ? 'ok' : 'soldout';
  }

  function likesRow(container, base, item, product, state) {
    var title = product ? String(product.title || '') : '';
    var variant = findVariant(product, item.vid);
    var key = entryKey(item.id, item.vid);

    var row = element('li', 'mb-likes__row mb-likes__row--' + state);
    row.setAttribute('data-mb-likes-row', '');
    row.setAttribute('data-mb-likes-key', key);
    row.setAttribute('data-mb-likes-id', item.id);
    row.setAttribute('data-mb-likes-variant-id', item.vid);
    row.setAttribute('data-mb-likes-title', title);

    /* チェックボックス */
    var checkCell = element('div', 'mb-likes__cell mb-likes__cell--check');
    var check = element('input', 'mb-likes__check');
    check.setAttribute('type', 'checkbox');
    check.setAttribute('data-mb-likes-check', '');
    var selectLabel = textOf(container, 'row-select', '選択');
    check.setAttribute('aria-label', title ? selectLabel + '：' + title : selectLabel);
    if (state === 'ok') {
      check.checked = !!selectionOf(container)['#' + key];
    } else {
      check.disabled = true;
      check.checked = false;
    }
    checkCell.appendChild(check);
    row.appendChild(checkCell);

    /* サムネイル */
    row.appendChild(buildThumb(base, product, item, title));

    /* 商品名 /（カラー サイズ）/ 価格 */
    var body = element('div', 'mb-likes__body');
    var name = element('p', 'mb-likes__name');
    var url = productUrl(base, product, item);
    if (product && url) {
      var link = element('a');
      link.setAttribute('href', url);
      link.textContent = title;
      name.appendChild(link);
    } else {
      name.textContent = title || textOf(container, 'unavailable', 'お取り扱い終了');
    }
    body.appendChild(name);

    var label = variantLabel(variant);
    if (label) {
      var option = element('p', 'mb-likes__option');
      option.textContent = '（' + label + '）';
      body.appendChild(option);
    }

    if (product) {
      var price = element('p', 'mb-likes__price');
      var amount = variant ? formatMoney(variant.price) : formatMoney(product.price);
      if (!variant && product.price_varies) {
        amount += '〜';
      }
      price.appendChild(document.createTextNode(amount));
      var tax = textOf(container, 'tax', '');
      if (tax) {
        var taxNote = element('span', 'mb-likes__tax');
        taxNote.textContent = tax;
        price.appendChild(taxNote);
      }
      body.appendChild(price);
    }

    if (state === 'pending') {
      body.appendChild(buildVariantSelect(container, product, item));
    }

    var note = element('p', 'mb-likes__note');
    note.setAttribute('data-mb-likes-note', '');
    if (state === 'soldout') {
      note.textContent = textOf(container, 'soldout', 'SOLD OUT');
    } else if (state === 'unavailable') {
      note.textContent = textOf(container, 'unavailable', 'お取り扱い終了');
    } else if (state === 'pending') {
      note.textContent = textOf(container, 'choose', 'バリアントを選択してください');
    } else {
      note.hidden = true;
    }
    body.appendChild(note);
    row.appendChild(body);

    /* 削除 */
    var removeCell = element('div', 'mb-likes__cell mb-likes__cell--remove');
    var removeButton = element('button', 'mb-likes__remove');
    removeButton.setAttribute('type', 'button');
    removeButton.setAttribute('data-mb-likes-remove', '');
    var removeLabel = textOf(container, 'remove', '削除');
    removeButton.textContent = removeLabel;
    removeButton.setAttribute('aria-label', title ? removeLabel + '：' + title : removeLabel);
    removeCell.appendChild(removeButton);
    row.appendChild(removeCell);

    return row;
  }

  /* handle が別商品に付け替えられている場合があるので、必ずIDの一致を確かめる */
  function fetchProduct(base, handle, productId) {
    if (!handle || typeof window.fetch !== 'function') {
      return Promise.resolve(null);
    }
    var url = base + 'products/' + encodeURIComponent(handle) + '.js';
    return window.fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response || !response.ok) {
          return null;
        }
        return response.json();
      })
      .then(function (product) {
        if (!product || normalizeId(product.id) !== productId) {
          return null;
        }
        return product;
      })
      .catch(function () {
        return null;
      });
  }

  /* 同じ商品の別サイズが並んでいても、商品の取得は1回で済ませる */
  function groupItems(items) {
    var groups = [];
    var index = {};
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var key = item.id + '@@' + item.handle;
      if (index[key] === undefined) {
        index[key] = groups.length;
        groups.push({ id: item.id, handle: item.handle, items: [] });
      }
      groups[index[key]].items.push(item);
    }
    return groups;
  }

  function fetchInBatches(base, groups, onResolved) {
    var cursor = 0;
    function next() {
      if (cursor >= groups.length) {
        return Promise.resolve();
      }
      var slice = groups.slice(cursor, cursor + FETCH_BATCH);
      cursor += FETCH_BATCH;
      return Promise.all(slice.map(function (group) {
        return fetchProduct(base, group.handle, group.id).then(function (product) {
          onResolved(group, product);
        });
      })).then(next);
    }
    return next();
  }

  function updateCounter(container) {
    var counter = container.querySelector('[data-mb-likes-count]');
    if (!counter) {
      return;
    }
    var total = count();
    counter.textContent = total ? withCount(textOf(container, 'count', '[count] 件'), total) : '';
  }

  function selectableRows(container) {
    var rows = nodeList(container, '[data-mb-likes-row]');
    var out = [];
    for (var i = 0; i < rows.length; i++) {
      var check = rows[i].querySelector('[data-mb-likes-check]');
      if (check && !check.disabled) {
        out.push(rows[i]);
      }
    }
    return out;
  }

  function checkedRows(container) {
    var rows = selectableRows(container);
    var out = [];
    for (var i = 0; i < rows.length; i++) {
      var check = rows[i].querySelector('[data-mb-likes-check]');
      if (check && check.checked) {
        out.push(rows[i]);
      }
    }
    return out;
  }

  /* 全選択チェックと投入ボタンの状態を、いまの選択に合わせる */
  function syncControls(container) {
    var selectable = selectableRows(container);
    var checked = checkedRows(container);
    var all = container.querySelector('[data-mb-likes-select-all]');
    if (all) {
      all.disabled = !selectable.length;
      all.checked = !!selectable.length && checked.length === selectable.length;
      if (typeof all.indeterminate === 'boolean') {
        all.indeterminate = checked.length > 0 && checked.length < selectable.length;
      }
    }
    var button = container.querySelector('[data-mb-likes-add-selected]');
    if (button && !container._mbLikesBusy) {
      button.disabled = !checked.length;
    }
    var toolbar = container.querySelector('[data-mb-likes-toolbar]');
    if (toolbar) {
      toolbar.hidden = !selectable.length;
    }
  }

  function showResult(container, message, tone) {
    var result = container.querySelector('[data-mb-likes-result]');
    if (!result) {
      return;
    }
    if (!message) {
      result.textContent = '';
      result.hidden = true;
      return;
    }
    result.textContent = message;
    result.hidden = false;
    result.className = 'mb-likes__result' + (tone ? ' mb-likes__result--' + tone : '');
  }

  function renderLikesPage(container) {
    if (!container) {
      return;
    }
    var listNode = container.querySelector('[data-mb-likes-list]');
    var empty = container.querySelector('[data-mb-likes-empty]');
    var actions = container.querySelector('[data-mb-likes-actions]');
    var toolbar = container.querySelector('[data-mb-likes-toolbar]');
    if (!listNode || !empty) {
      return;
    }
    /* 取得手段のない古い環境では、壊れた見た目を出さずに空状態のままにする */
    if (typeof window.fetch !== 'function' || typeof window.Promise !== 'function') {
      return;
    }

    var items = list();
    updateCounter(container);
    listNode.innerHTML = '';

    if (!items.length) {
      listNode.hidden = true;
      empty.hidden = false;
      if (actions) {
        actions.hidden = true;
      }
      if (toolbar) {
        toolbar.hidden = true;
      }
      showResult(container, '');
      return;
    }
    empty.hidden = true;
    listNode.hidden = false;
    if (actions) {
      actions.hidden = false;
    }

    /* 取得を待つあいだも高さを確保しておき、表示のがたつきを防ぐ */
    var slots = {};
    for (var i = 0; i < items.length; i++) {
      var slot = placeholderRow();
      slots['#' + entryKey(items[i].id, items[i].vid)] = slot;
      listNode.appendChild(slot);
    }
    syncControls(container);

    var base = rootUrl(container);
    fetchInBatches(base, groupItems(items), function (group, product) {
      /* 単一バリアントの商品は、未確定のまま置かずにその場で確定させる */
      if (product && isArray(product.variants) && product.variants.length === 1) {
        for (var p = 0; p < group.items.length; p++) {
          var pendingItem = group.items[p];
          if (pendingItem.vid) {
            continue;
          }
          var outcome = resolveVariant(pendingItem.id, product.variants[0].id);
          if (outcome === 'merged') {
            /* 同じ行がすでに在るので、この行は出さずに畳む */
            pendingItem.merged = true;
          } else if (outcome === 'resolved') {
            pendingItem.vid = normalizeId(product.variants[0].id);
          }
        }
      }
      for (var n = 0; n < group.items.length; n++) {
        var item = group.items[n];
        var slotKey = '#' + entryKey(item.id, item.merged ? '' : item.vid);
        var slot = slots[slotKey] || slots['#' + entryKey(item.id, '')];
        if (!slot || !slot.parentNode) {
          continue;
        }
        if (item.merged) {
          slot.parentNode.removeChild(slot);
          continue;
        }
        slot.parentNode.replaceChild(likesRow(container, base, item, product, rowState(product, item)), slot);
      }
      updateCounter(container);
      syncControls(container);
    });
  }

  /* ----------------------------------------------------------------------
     一括でBAGに入れる
     まとめて1回で送ると、1点でも弾かれたとき全部が入らず、どれが駄目だったかも
     返せない。1点ずつ順番に送り、成功した分と失敗した分を分けて返す。
     ---------------------------------------------------------------------- */

  function sequence(items, worker) {
    var cursor = 0;
    function step() {
      if (cursor >= items.length) {
        return Promise.resolve();
      }
      var current = items[cursor];
      cursor++;
      return worker(current).then(step);
    }
    return step();
  }

  function failureReason(container, body) {
    var text = body && (body.description || body.message);
    if (text) {
      return String(text);
    }
    return textOf(container, 'add-error', 'この商品はただいまお求めいただけません。');
  }

  function addOne(container, base, row) {
    var vid = normalizeId(row.getAttribute('data-mb-likes-variant-id'));
    var title = row.getAttribute('data-mb-likes-title') || '';
    if (!vid) {
      return Promise.resolve({ ok: false, row: row, title: title, reason: failureReason(container, null) });
    }
    return window.fetch(base + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] })
    }).then(function (response) {
      var ok = !!(response && response.ok);
      return response.json().then(function (body) {
        return { ok: ok, body: body };
      }).catch(function () {
        return { ok: ok, body: null };
      }).then(function (parsed) {
        if (parsed.ok) {
          return { ok: true, row: row, title: title };
        }
        /* 在庫不足・上限超過はここに来る。理由はShopifyの返答をそのまま出す */
        return { ok: false, row: row, title: title, reason: failureReason(container, parsed.body) };
      });
    }).catch(function () {
      return { ok: false, row: row, title: title, reason: failureReason(container, null) };
    });
  }

  /* テーマ既定のカート更新経路に乗せる。ヘッダーの点数表示もこれで追従する */
  function refreshCart(base) {
    return window.fetch(base + 'cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response && response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (!cart || typeof window.CustomEvent !== 'function') {
          return;
        }
        document.dispatchEvent(new window.CustomEvent(CART_CHANGE_EVENT, {
          bubbles: true,
          detail: { cart: cart }
        }));
      })
      .catch(function () {});
  }

  function setBusy(container, busy) {
    container._mbLikesBusy = busy;
    var button = container.querySelector('[data-mb-likes-add-selected]');
    if (!button) {
      return;
    }
    button.disabled = busy;
    if (busy) {
      if (!button._mbLabel) {
        button._mbLabel = button.textContent;
      }
      var adding = textOf(container, 'adding', '');
      if (adding) {
        button.textContent = adding;
      }
      button.setAttribute('aria-busy', 'true');
    } else {
      if (button._mbLabel) {
        button.textContent = button._mbLabel;
      }
      button.removeAttribute('aria-busy');
    }
  }

  function reportResults(container, succeeded, failed) {
    var parts = [];
    if (succeeded.length) {
      parts.push(withCount(textOf(container, 'added', '[count]点をBAGに入れました。'), succeeded.length));
    }
    if (failed.length) {
      parts.push(withCount(textOf(container, 'failed', '[count]点は追加できませんでした。'), failed.length));
    }
    showResult(container, parts.join(' '), failed.length ? 'error' : 'ok');

    for (var s = 0; s < succeeded.length; s++) {
      var okRow = succeeded[s];
      var okCheck = okRow.row.querySelector('[data-mb-likes-check]');
      if (okCheck) {
        okCheck.checked = false;
      }
      delete selectionOf(container)['#' + okRow.row.getAttribute('data-mb-likes-key')];
      if (okRow.row.classList) {
        okRow.row.classList.add('is-added');
      }
    }
    /* 失敗した行には、その行の理由をその場に出す */
    for (var f = 0; f < failed.length; f++) {
      var note = failed[f].row.querySelector('[data-mb-likes-note]');
      if (note) {
        note.textContent = failed[f].reason;
        note.hidden = false;
      }
    }
    syncControls(container);
  }

  function addSelected(container) {
    if (container._mbLikesBusy || typeof window.fetch !== 'function' || typeof window.Promise !== 'function') {
      return;
    }
    var rows = checkedRows(container);
    if (!rows.length) {
      showResult(container, textOf(container, 'select-items', '商品を選択してください。'), 'error');
      return;
    }
    var queue = rows.slice(0, ADD_LIMIT);
    var base = rootUrl(container);
    var succeeded = [];
    var failed = [];
    setBusy(container, true);
    showResult(container, '');
    sequence(queue, function (row) {
      return addOne(container, base, row).then(function (result) {
        (result.ok ? succeeded : failed).push(result);
      });
    }).then(function () {
      return succeeded.length ? refreshCart(base) : null;
    }).then(function () {
      setBusy(container, false);
      reportResults(container, succeeded, failed);
    }).catch(function () {
      setBusy(container, false);
      showResult(container, textOf(container, 'add-error', 'この商品はただいまお求めいただけません。'), 'error');
    });
  }

  /* ----------------------------------------------------------------------
     一覧ページの操作
     ---------------------------------------------------------------------- */

  function likesContainers() {
    if (!canUseDom()) {
      return [];
    }
    return nodeList(document, '[data-mb-likes-root]');
  }

  function onLikesClick(event) {
    var removeButton = closestMatch(event.target, 'data-mb-likes-remove');
    if (removeButton) {
      var row = closestMatch(removeButton, 'data-mb-likes-row');
      var container = closestMatch(removeButton, 'data-mb-likes-root');
      if (!row || !container) {
        return;
      }
      event.preventDefault();
      var id = normalizeId(row.getAttribute('data-mb-likes-id'));
      var vid = normalizeId(row.getAttribute('data-mb-likes-variant-id'));
      delete selectionOf(container)['#' + row.getAttribute('data-mb-likes-key')];
      remove(id, vid);
      showToast(textOf(container, 'removed', ''));
      notify({ id: id, vid: vid, handle: '', liked: false, count: count(), source: 'likes' });
      return;
    }
    var addButton = closestMatch(event.target, 'data-mb-likes-add-selected');
    if (addButton) {
      event.preventDefault();
      var root = closestMatch(addButton, 'data-mb-likes-root');
      if (root) {
        addSelected(root);
      }
    }
  }

  function onLikesChange(event) {
    var target = event && event.target;
    if (!target || !target.getAttribute) {
      return;
    }
    var container = closestMatch(target, 'data-mb-likes-root');
    if (!container) {
      return;
    }
    if (target.hasAttribute('data-mb-likes-check')) {
      var row = closestMatch(target, 'data-mb-likes-row');
      if (row) {
        var key = '#' + row.getAttribute('data-mb-likes-key');
        if (target.checked) {
          selectionOf(container)[key] = true;
        } else {
          delete selectionOf(container)[key];
        }
      }
      syncControls(container);
      return;
    }
    if (target.hasAttribute('data-mb-likes-select-all')) {
      var rows = selectableRows(container);
      for (var i = 0; i < rows.length; i++) {
        var check = rows[i].querySelector('[data-mb-likes-check]');
        if (!check) {
          continue;
        }
        check.checked = !!target.checked;
        var rowKey = '#' + rows[i].getAttribute('data-mb-likes-key');
        if (target.checked) {
          selectionOf(container)[rowKey] = true;
        } else {
          delete selectionOf(container)[rowKey];
        }
      }
      syncControls(container);
      return;
    }
    if (target.hasAttribute('data-mb-likes-variant')) {
      var pendingRow = closestMatch(target, 'data-mb-likes-row');
      if (!pendingRow || !target.value) {
        return;
      }
      var productId = normalizeId(pendingRow.getAttribute('data-mb-likes-id'));
      if (resolveVariant(productId, target.value)) {
        syncLikeBadges();
        renderLikesPage(container);
      }
    }
  }

  function renderAllLikesPages() {
    var containers = likesContainers();
    for (var i = 0; i < containers.length; i++) {
      renderLikesPage(containers[i]);
    }
  }

  /* 一覧ページで解除されたら、その場で行も消す */
  function onChange(event) {
    var detail = event && event.detail;
    if (!detail || detail.liked) {
      return;
    }
    var containers = likesContainers();
    for (var i = 0; i < containers.length; i++) {
      renderLikesPage(containers[i]);
    }
  }

  /* ======================================================================
     起動
     ====================================================================== */

  var cartObserver = null;

  function setBadgeValue(selector, value) {
    var badges = document.querySelectorAll(selector);
    var display = value > 99 ? '99+' : String(value);
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = display;
      badges[i].hidden = value < 1;
    }
  }

  function syncLikeBadges() {
    if (!canUseDom()) {
      return;
    }
    setBadgeValue('[data-mb-likes-badge]', count());
  }

  function cartCountFromHeader() {
    var source = document.querySelector('[data-header-cart-count]');
    if (!source) {
      return 0;
    }
    var raw = source.getAttribute('data-header-cart-count') || source.textContent || '0';
    var match = String(raw).match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  }

  function syncCartBadges() {
    if (!canUseDom()) {
      return;
    }
    setBadgeValue('[data-mb-cart-badge]', cartCountFromHeader());
  }

  function mutationContainsCartCount(mutation) {
    var node = mutation && mutation.target;
    if (node && node.nodeType === 3) {
      node = node.parentNode;
    }
    if (!node || !node.getAttribute) {
      return false;
    }
    if (node.hasAttribute('data-header-cart-count')) {
      return true;
    }
    return !!(mutation.type === 'childList' && node.querySelector && node.querySelector('[data-header-cart-count]'));
  }

  function observeCartCount() {
    if (!canUseDom() || typeof window.MutationObserver !== 'function') {
      return;
    }
    var header = document.querySelector('[data-header-wrapper]');
    if (!header) {
      return;
    }
    if (cartObserver) {
      cartObserver.disconnect();
    }
    cartObserver = new window.MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutationContainsCartCount(mutations[i])) {
          syncCartBadges();
          return;
        }
      }
    });
    cartObserver.observe(header, {
      attributes: true,
      attributeFilter: ['data-header-cart-count'],
      characterData: true,
      childList: true,
      subtree: true
    });
  }

  function refresh() {
    syncVariantHearts(document, '');
    syncButtons();
    syncLikeBadges();
    syncCartBadges();
    observeCartCount();
    renderAllLikesPages();
  }

  function init() {
    if (!canUseDom()) {
      return;
    }
    document.addEventListener('click', onClick);
    document.addEventListener('click', onLikesClick);
    document.addEventListener('change', onLikesChange);
    document.addEventListener(CHANGE_EVENT, onChange);
    document.addEventListener(CHANGE_EVENT, syncLikeBadges);
    document.addEventListener('cart:updated', syncCartBadges);
    document.addEventListener('cart:change', syncCartBadges);
    document.addEventListener(CART_CHANGE_EVENT, syncCartBadges);
    /* 商品ページでサイズ・カラーが切り替わったら、星の状態もその場で追従させる */
    document.addEventListener('theme:variant:change', onVariantChange);
    document.addEventListener('variant:change', onVariantChange);
    /* 別タブでの追加・解除を反映する */
    window.addEventListener('storage', function (event) {
      if (event && event.key && event.key !== STORAGE_KEY) {
        return;
      }
      refresh();
    });
    /* テーマエディタでセクションが差し替わったとき */
    document.addEventListener('shopify:section:load', function () {
      refresh();
    });
    refresh();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', refresh);
    }
  }

  window.MarblesHeartLikes = {
    STORAGE_KEY: STORAGE_KEY,
    LEGACY_STORAGE_KEY: LEGACY_STORAGE_KEY,
    VERSION: SCHEMA_VERSION,
    CHANGE_EVENT: CHANGE_EVENT,
    MAX_ITEMS: MAX_ITEMS,
    list: list,
    ids: ids,
    keys: keys,
    count: count,
    has: has,
    hasProduct: hasProduct,
    add: add,
    remove: remove,
    toggle: toggle,
    resolveVariant: resolveVariant,
    clear: clear,
    refresh: refresh,
    formatMoney: formatMoney
  };

  init();
}(window, document));
